"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getProviderClinicAvailability = exports.isClinicOpen = exports.sortHoursListByWeekdaysOrder = exports.getCurrentDay = exports.getWeekdays = exports.getGoogleMapLink = exports.getContactDetails = void 0;
const moment_1 = __importDefault(require("moment"));
const messages_1 = __importDefault(require("../../lib/messages"));
const getContactDetails = (location, contactType) => {
    const contacts = (location.contacts || []).find((item) => item.contact_type === contactType);
    return ((contacts === null || contacts === void 0 ? void 0 : contacts.value) ||
        location[contactType]);
};
exports.getContactDetails = getContactDetails;
const getGoogleMapLink = (location) => {
    // The parameter api=1 is required in every request and the only valid value currently is 1.
    // If api=1 is not present in the URL, all parameters are ignored
    // Docs: https://developers.google.com/maps/documentation/urls/get-started#forming-the-url
    const baseMapUrl = 'https://www.google.com/maps/search/?api=1&query=';
    const addressFields = ['street1', 'street2', 'suite', 'city', 'state', 'zip'];
    const destinationAddress = addressFields
        .map((field) => location[field])
        .filter((i) => !!i)
        .join(', ');
    const locationName = location.exterior_signage_name || location.name;
    const locationURI = encodeURIComponent(locationName ? `${locationName}, ${destinationAddress}` : destinationAddress);
    return `${baseMapUrl}${locationURI}`;
};
exports.getGoogleMapLink = getGoogleMapLink;
/** Returns array of strings - weekdays list
 * output - ['Monday', 'Tuesday', ...]
 * @param {string} format i.e 'dddd'
 * */
const getWeekdays = (format) => [...Array(7).keys()].map(function (_, i) {
    return (0, moment_1.default)(i, 'e')
        .startOf('week')
        .isoWeekday(i + 1)
        .format(format);
});
exports.getWeekdays = getWeekdays;
/** Returns current day
 * output - 'Monday'
 * @param {string} format i.e 'dddd'
 * */
const getCurrentDay = (format) => {
    const weekdays = (0, exports.getWeekdays)(format);
    const currentDayIndex = (0, moment_1.default)().day();
    return weekdays[(currentDayIndex || weekdays.length) - 1];
};
exports.getCurrentDay = getCurrentDay;
/** Returns sorted mloc periods in given weekdays order
 * output - {
 *     {openDay: Monday, ...},
 *     {openDay: Tuesday, ...},
 *     ....
 * }
 * @param {array} hoursList - provider mlocs hours data
 * @param {string} weekdays - weekdays list ['Monday', 'Tuesday', ...]
 * */
const sortHoursListByWeekdaysOrder = (hoursList, weekdays) => {
    const ordering = {};
    for (let i = 0; i < weekdays.length; i++)
        ordering[weekdays[i]] = i;
    return hoursList.sort(function (a, b) {
        return (ordering[a.openDay] - ordering[b.openDay] ||
            a.openTime.localeCompare(b.openTime));
    });
};
exports.sortHoursListByWeekdaysOrder = sortHoursListByWeekdaysOrder;
/** Returns object - find availability in mloc data based on current day/time
 * output - {
 *     isOpenNow: {} | undefined,
 *     willOpenNextToday: {} | undefined,
 * }
 * @param {array} data - provider mlocs hours data
 * @param {string} time - current time
 * */
const isClinicOpen = (data, time) => {
    const isOpenNow = data.find((t) => time >= t.openTime && time <= t.closeTime);
    const willOpenNextToday = data.find((t) => time < t.openTime);
    return {
        isOpenNow,
        willOpenNextToday
    };
};
exports.isClinicOpen = isClinicOpen;
const convert24To12HoursFormat = (format) => (0, moment_1.default)(format, 'HH:mm').format('hh:mm A');
/** Returns Array of strings
 * output - ['Closed', 'Open Next {available time slot}']
 * @param {array} hoursList - provider mlocs hours data
 * @param {array} weekdaysList - weekdays list ['Monday', 'Tuesday', ...]
 * */
const getNextDayAvailability = (hoursList, weekdaysList, day, intl) => {
    let getNextDay;
    const todayIndex = weekdaysList.indexOf(day) + 1;
    const slicePastDays = weekdaysList.slice(todayIndex, weekdaysList.length);
    getNextDay = hoursList.filter((t) => slicePastDays.includes(t.openDay));
    if (!getNextDay.length) {
        getNextDay = hoursList.filter((t) => weekdaysList.includes(t.openDay));
    }
    const validGetNextDay = Array.isArray(getNextDay) && getNextDay[0] ? getNextDay[0] : null;
    if (!validGetNextDay) {
        return;
    }
    const nextAvailDay = validGetNextDay.openDay;
    const nextAvailTime = convert24To12HoursFormat(validGetNextDay.openTime || '');
    return [
        `${intl.formatMessage(messages_1.default.closed)}`,
        `${intl.formatMessage(messages_1.default.opensNext, {
            nextAvailDay,
            nextAvailTime
        })}`
    ];
};
/** Returns Array of string to be render on client side
 * output - ['Open until {time}'] or ['Closed', 'Open Next {available time slot}']
 * @param {array} periods - provider mlocs hours data
 * @param {string} day - current day
 * @param {string} time - current time
 * */
const getProviderClinicAvailability = (periods, day, time, intl) => {
    // sanitizing data here because we source it from external systems and it may be bad
    const sanitizedPeriods = (() => {
        if (Array.isArray(periods)) {
            return periods.filter((p) => {
                return (Boolean(p) &&
                    typeof p === 'object' &&
                    (Boolean(p.openDay) || Boolean(p.openTime)));
            });
        }
        return [];
    })();
    const weekdaysList = (0, exports.getWeekdays)('dddd');
    const sortedHoursList = (0, exports.sortHoursListByWeekdaysOrder)(sanitizedPeriods, weekdaysList);
    const filterPresentDayObj = sortedHoursList.filter((i) => i.openDay === day);
    const checkAvailability = (0, exports.isClinicOpen)(filterPresentDayObj, time);
    if (checkAvailability.isOpenNow) {
        const openUntilTime = convert24To12HoursFormat(checkAvailability.isOpenNow.closeTime);
        return [`${intl.formatMessage(messages_1.default.openUntil, { openUntilTime })}`];
    }
    if (checkAvailability.willOpenNextToday) {
        const openNextTime = convert24To12HoursFormat(checkAvailability.willOpenNextToday.openTime);
        return [
            `${intl.formatMessage(messages_1.default.closed)}`,
            `${intl.formatMessage(messages_1.default.openNextToday, { openNextTime })}`
        ];
    }
    if (!checkAvailability.isOpenNow && !checkAvailability.willOpenNextToday) {
        return getNextDayAvailability(sortedHoursList, weekdaysList, day, intl);
    }
    return;
};
exports.getProviderClinicAvailability = getProviderClinicAvailability;
//# sourceMappingURL=utils.js.map